/**
 * ImportPreview - Preview and validation component for CSV import
 * 
 * Features:
 * - Summary stats (valid, invalid, duplicate counts)
 * - Data preview table (first 50 rows)
 * - Validation settings (skip invalid, duplicate handling)
 * - Row status indicators
 */

import { useMemo } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Copy,
  ArrowLeft,
  Upload,
  FileSpreadsheet,
  Info,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { DUPLICATE_MODE, getImportRowErrorMessage } from '../hooks/useImport';

// Max rows to show in preview
const PREVIEW_ROW_LIMIT = 50;

/**
 * ImportPreview component
 * 
 * @param {Object} props
 * @param {Object} props.preview - Preview data from backend
 * @param {Object} props.settings - Import settings (duplicateMode, skipInvalidRows)
 * @param {Function} props.onUpdateSettings - Update settings
 * @param {Function} props.onConfirm - Confirm and start import
 * @param {Function} props.onBack - Go back to upload step
 * @param {boolean} props.confirming - Whether confirm is in progress
 * @param {number} props.importableCount - Number of rows that will be imported
 * @param {string|null} props.error - Error of a refused confirm: a key of t.import.confirmErrors or the backend's text
 */
export function ImportPreview({
  preview,
  settings,
  onUpdateSettings,
  onConfirm,
  onBack,
  confirming = false,
  importableCount = 0,
  error = null,
}) {
  const { t } = useLanguage();

  // Get row status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'valid':
        return <CheckCircle2 className="h-4 w-4 text-emerald-500" />;
      case 'invalid':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'duplicate':
        return <Copy className="h-4 w-4 text-amber-500" />;
      case 'warning':
        return <AlertTriangle className="h-4 w-4 text-amber-500" />;
      default:
        return null;
    }
  };

  // Get status label
  const getStatusLabel = (status) => {
    const labels = {
      valid: t.import?.statusValid || 'Valid',
      invalid: t.import?.statusInvalid || 'Invalid',
      duplicate: t.import?.statusDuplicate || 'Duplicate',
      warning: t.import?.statusWarning || 'Warning',
    };
    return labels[status] || status;
  };

  // Get status badge color
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'valid':
        return 'bg-emerald-100 text-emerald-700';
      case 'invalid':
        return 'bg-red-100 text-red-700';
      case 'duplicate':
        return 'bg-amber-100 text-amber-700';
      case 'warning':
        return 'bg-amber-100 text-amber-700';
      default:
        return 'bg-slate-100 text-slate-700';
    }
  };

  // Preview rows limited
  const previewRows = useMemo(() => {
    if (!preview?.rows) return [];
    return preview.rows.slice(0, PREVIEW_ROW_LIMIT);
  }, [preview]);

  // Get column headers from first row
  const columns = useMemo(() => {
    if (!previewRows.length || !previewRows[0].data) return [];
    return Object.keys(previewRows[0].data);
  }, [previewRows]);

  // Check if we can import
  const canImport = importableCount > 0;

  // Text of a refused confirm (useImport keeps a key of t.import.confirmErrors or the backend's own text)
  const confirmErrorMessage = error
    && (t.import?.confirmErrors?.[error]?.replace('{count}', preview?.duplicate_count) || error);

  if (!preview) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="border-l-4 border-l-slate-400">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">{t.import?.totalRows || 'Total Rows'}</p>
            <p className="text-2xl font-bold text-slate-700">{preview.total_rows}</p>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">{t.import?.validRows || 'Valid'}</p>
            <p className="text-2xl font-bold text-emerald-600">{preview.valid_count}</p>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-red-500">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">{t.import?.invalidRows || 'Invalid'}</p>
            <p className="text-2xl font-bold text-red-600">{preview.invalid_count}</p>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">{t.import?.duplicateRows || 'Duplicates'}</p>
            <p className="text-2xl font-bold text-amber-600">{preview.duplicate_count}</p>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-amber-400">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">{t.import?.warningRows || 'Warnings'}</p>
            <p className="text-2xl font-bold text-amber-500">{preview.warning_count || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Validation Settings */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-slate-500" />
            {t.import?.validationSettings || 'Validation Settings'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Skip invalid rows */}
          {preview.invalid_count > 0 && (
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Switch
                  id="skip-invalid"
                  checked={settings.skipInvalidRows}
                  onCheckedChange={(checked) => onUpdateSettings({ skipInvalidRows: checked })}
                  data-testid="skip-invalid-switch"
                />
                <Label htmlFor="skip-invalid" className="cursor-pointer">
                  {t.import?.skipInvalidRows || 'Skip invalid rows'}
                </Label>
              </div>
              <span className="text-sm text-slate-500">
                {t.import?.skipInvalidRowsHint?.replace('{count}', preview.invalid_count) || 
                  `${preview.invalid_count} rows will be skipped`}
              </span>
            </div>
          )}

          {/* Duplicate handling */}
          {preview.duplicate_count > 0 && (
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Label className="text-sm font-medium">
                  {t.import?.duplicateHandling || 'Duplicate handling'}
                </Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <Info className="h-4 w-4 text-slate-400" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs text-sm">
                        {preview.duplicate_count} {t.import?.duplicateRows || 'duplicates'} found
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <Select
                value={settings.duplicateMode}
                onValueChange={(value) => onUpdateSettings({ duplicateMode: value })}
              >
                <SelectTrigger className="w-48" data-testid="duplicate-mode-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={DUPLICATE_MODE.SKIP}>
                    {t.import?.duplicateModeSkip || 'Skip duplicates'}
                  </SelectItem>
                  <SelectItem value={DUPLICATE_MODE.UPDATE}>
                    {t.import?.duplicateModeUpdate || 'Update existing'}
                  </SelectItem>
                  <SelectItem value={DUPLICATE_MODE.FAIL}>
                    {t.import?.duplicateModeFail || 'Stop on duplicate'}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center justify-between">
            <span className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-slate-500" />
              {t.import?.previewTable || 'Data Preview'}
            </span>
            <span className="text-sm font-normal text-slate-500">
              {previewRows.length < preview.total_rows 
                ? `${previewRows.length} / ${preview.total_rows} rows`
                : `${preview.total_rows} rows`}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="w-16 text-center">{t.import?.rowNumber || '#'}</TableHead>
                  <TableHead className="w-24">{t.import?.rowStatus || 'Status'}</TableHead>
                  {columns.map((col) => (
                    <TableHead key={col} className="whitespace-nowrap">
                      {col}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {previewRows.map((row, idx) => (
                  <TableRow 
                    key={idx}
                    className={cn(
                      row.status === 'invalid' && 'bg-red-50/50',
                      row.status === 'duplicate' && 'bg-amber-50/50'
                    )}
                    data-testid={`preview-row-${idx}`}
                  >
                    <TableCell className="text-center text-sm text-slate-500">
                      {row.row_number || idx + 1}
                    </TableCell>
                    <TableCell>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="flex items-center gap-1">
                              <Badge className={cn('gap-1', getStatusBadgeClass(row.status))}>
                                {getStatusIcon(row.status)}
                                <span className="text-xs">{getStatusLabel(row.status)}</span>
                              </Badge>
                            </div>
                          </TooltipTrigger>
                          {(row.errors?.length > 0 || row.duplicate_of) && (
                            // Above the wizard dialog, whose overlay and content sit at z-index 9998/9999 (ui/dialog.jsx)
                            <TooltipContent side="right" className="max-w-sm z-[10000]">
                              {row.errors?.map((err, i) => (
                                <p key={i} className="text-sm">
                                  {err.field}: {getImportRowErrorMessage(t, err)}
                                </p>
                              ))}
                              {row.duplicate_of && (
                                <p className="text-sm">
                                  {t.import?.duplicateOf || 'Duplicate of'}: {row.duplicate_of}
                                </p>
                              )}
                            </TooltipContent>
                          )}
                        </Tooltip>
                      </TooltipProvider>
                    </TableCell>
                    {columns.map((col) => (
                      <TableCell 
                        key={col} 
                        className={cn(
                          'text-sm max-w-xs truncate',
                          row.errors?.some(e => e.field === col) && 'text-red-600 font-medium'
                        )}
                      >
                        {row.data?.[col] ?? '-'}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Refused confirm, next to the button that sent it */}
      {confirmErrorMessage && (
        <div
          role="alert"
          className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg"
          data-testid="import-confirm-error"
        >
          <XCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div className="space-y-2">
            <p className="text-sm text-red-700">{confirmErrorMessage}</p>
            {error === 'previewExpired' && (
              <Button
                variant="outline"
                size="sm"
                onClick={onBack}
                className="gap-2"
                data-testid="preview-expired-upload-btn"
              >
                <Upload className="h-4 w-4" />
                {t.import?.uploadAgain || 'Upload the file again'}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between pt-2">
        <Button
          variant="outline"
          onClick={onBack}
          disabled={confirming}
          className="gap-2"
          data-testid="preview-back-btn"
        >
          <ArrowLeft className="h-4 w-4" />
          {t.import?.back || 'Back'}
        </Button>

        <div className="flex items-center gap-3">
          {!canImport && (
            <span className="text-sm text-slate-500">
              {t.import?.noValidRows || 'No valid rows to import'}
            </span>
          )}
          <Button
            onClick={onConfirm}
            disabled={!canImport || confirming}
            className="gap-2 min-w-32"
            data-testid="confirm-import-btn"
          >
            {confirming ? (
              <>
                <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                {t.common?.processing || 'Processing...'}
              </>
            ) : (
              <>
                <Upload className="h-4 w-4" />
                {t.import?.importCount?.replace('{count}', importableCount) || 
                  `Import (${importableCount})`}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ImportPreview;
